import React, { useCallback } from 'react';
import {
  PortfolioStrategyFilterType,
  usePortfolioMultiFiltering,
  usePortfolioMultiFilteringWithSync,
} from 'venn-components';
import { Icon, WarningNote } from 'venn-ui-kit';
import { type BlockId } from 'venn-state';

import { SmallSwitch } from '@/components/ui/small-switch';
import { cn } from '@/lib/utils';

import { PanelSection } from '../../../PanelSection';
import { PortfolioMultiLevelDropdown } from './components/PortfolioMultiLevelDropdown';

interface FormatSectionProps {
  selected: BlockId;
  isReadOnly?: boolean;
}

const getLevelLabel = (level: string | number) =>
  level === 0 ? 'Top Level Strategies' : level === 'investments' ? 'Investments' : `Sub-strategies Level ${level}`;

const SelectedMultiLevelList = ({ blockId, isReadOnly }: { blockId: BlockId; isReadOnly: boolean }) => {
  const { hasMultiLevelPortfolioFiltering, filter, setFilter } = usePortfolioMultiFiltering(blockId);

  const handleDelete = useCallback(
    (level: string | number) => () => {
      setFilter({
        ...filter,
        filterType: PortfolioStrategyFilterType.CUSTOM_SELECTION,
        levels: filter.levels.filter((l) => l !== level),
      });
    },
    [filter, setFilter],
  );

  if (!hasMultiLevelPortfolioFiltering) {
    return null;
  }

  const { levels } = filter;

  return (
    <ul className={cn('list-none flex flex-col p-0 mt-[10px] mx-[-20px]')}>
      {levels.map((level) => (
        <li
          key={level}
          className={cn('flex justify-between items-center px-[20px] outline-gray-300 hover:bg-gray-100 hover:outline')}
        >
          <div className={cn('border-solid border-2 border-y-0 border-r-0 border-l-venn-grey pl-[15px] py-[3px]')}>
            {getLevelLabel(level)}
          </div>
          {!isReadOnly && (
            <div>
              <Icon
                type="trash"
                className={cn('text-venn-grey hover:text-venn-primary-dark hover:cursor-pointer')}
                onClick={handleDelete(level)}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

const StrategyLevelSection = ({ selected, isReadOnly }: FormatSectionProps) => {
  const { hasMultiLevelPortfolioFiltering, supportsMergeLevels, filter, toggleMergeRows, wereLevelsFiltered } =
    usePortfolioMultiFilteringWithSync(selected);

  if (!hasMultiLevelPortfolioFiltering) {
    return null;
  }

  return (
    <PanelSection header="Strategy Levels">
      <div className="pt-[10px] w-full">
        <PortfolioMultiLevelDropdown blockId={selected} isReadOnly={!!isReadOnly} />
        <SelectedMultiLevelList blockId={selected} isReadOnly={!!isReadOnly} />

        {wereLevelsFiltered && (
          <WarningNote size="small" className="mt-[10px]">
            Some previously-selected strategy levels are no longer present in portfolios assigned to this block.
          </WarningNote>
        )}

        {supportsMergeLevels && (
          <div className="mt-[10px] font-bold text-[12px] flex items-center justify-between w-full">
            <span className="height-[100%]">Merge matching names</span>
            <SmallSwitch
              checked={!!filter?.mergeRows}
              onCheckedChange={toggleMergeRows}
              disabled={isReadOnly}
              data-testid="qa-filtering-merge-toggle"
            />
          </div>
        )}
      </div>
    </PanelSection>
  );
};

export default StrategyLevelSection;
